import { Suspense, lazy, useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { AppLayout } from '@/components/AppLayout'
import { PageLoader } from '@/components/ui'
import { api } from '@/lib/api'
import type { MeResponse } from '@/lib/types'
import { AcceptInvitePage } from '@/pages/AcceptInvitePage'
import { DatasetVersionPage } from '@/pages/DatasetVersionPage'
import { DatasetVersionsPage } from '@/pages/DatasetVersionsPage'
import { LoginPage } from '@/pages/LoginPage'
import { MappingPage } from '@/pages/MappingPage'
import { MembersPage } from '@/pages/MembersPage'
import { RegisterPage } from '@/pages/RegisterPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { SourcesPage } from '@/pages/SourcesPage'
import { WorkspaceHomePage } from '@/pages/WorkspaceHomePage'
import { useAuthStore } from '@/stores/auth'

// Экраны дашбордов загружаются отдельным чанком: они тянут библиотеку графиков
// и сетку, которые втрое увеличивают бандл. Экран входа не должен ждать
// загрузки того, что понадобится только внутри приложения.
const DashboardsPage = lazy(() =>
  import('@/pages/DashboardsPage').then((module) => ({ default: module.DashboardsPage })),
)
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((module) => ({ default: module.DashboardPage })),
)
const AddWidgetPage = lazy(() =>
  import('@/pages/AddWidgetPage').then((module) => ({ default: module.AddWidgetPage })),
)
const AskAIPage = lazy(() =>
  import('@/pages/AskAIPage').then((module) => ({ default: module.AskAIPage })),
)
const DatasetProposalsPage = lazy(() =>
  import('@/pages/DatasetProposalsPage').then((module) => ({
    default: module.DatasetProposalsPage,
  })),
)

/**
 * Восстановление сессии при загрузке приложения.
 *
 * Access-токен живёт только в памяти, поэтому после перезагрузки страницы
 * его нет. Пытаемся обновить сессию по HttpOnly cookie: если получилось —
 * пользователь продолжает работу, если нет — попадает на экран входа.
 */
function useRestoreSession(): void {
  const setRestoring = useAuthStore((state) => state.setRestoring)
  const setSession = useAuthStore((state) => state.setSession)
  const clearSession = useAuthStore((state) => state.clearSession)

  useEffect(() => {
    let cancelled = false

    void (async () => {
      const refreshed = await api.refreshSession()
      if (cancelled) return

      if (!refreshed) {
        clearSession()
        return
      }

      try {
        const me = await api.get<MeResponse>('/auth/me', { skipWorkspace: true })
        if (!cancelled) setSession(me.user, me.workspaces)
      } catch {
        if (!cancelled) clearSession()
      } finally {
        if (!cancelled) setRestoring(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [clearSession, setRestoring, setSession])
}

/** Обёртка ленивых маршрутов: пока чанк загружается, показывается загрузчик. */
function Lazy({ element }: { element: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{element}</Suspense>
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const isRestoring = useAuthStore((state) => state.isRestoring)
  const isAuthenticated = useAuthStore((state) => state.accessToken !== null && state.user !== null)
  const location = useLocation()

  if (isRestoring) return <PageLoader label="Проверяем сессию…" />

  if (!isAuthenticated) {
    // Запомним, куда пользователь шёл, чтобы вернуть его туда после входа
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }

  return <>{children}</>
}

function RedirectIfAuthenticated({ children }: { children: React.ReactNode }) {
  const isRestoring = useAuthStore((state) => state.isRestoring)
  const isAuthenticated = useAuthStore((state) => state.accessToken !== null && state.user !== null)

  if (isRestoring) return <PageLoader label="Проверяем сессию…" />
  if (isAuthenticated) return <Navigate to="/" replace />
  return <>{children}</>
}

export function App() {
  useRestoreSession()

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <RedirectIfAuthenticated>
            <LoginPage />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/register"
        element={
          <RedirectIfAuthenticated>
            <RegisterPage />
          </RedirectIfAuthenticated>
        }
      />
      {/* Приглашение открывается по ссылке из письма: страница сама решит,
          нужно ли сначала войти или зарегистрироваться */}
      <Route path="/invite/:token" element={<AcceptInvitePage />} />

      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route path="/" element={<WorkspaceHomePage />} />
        <Route path="/sources" element={<SourcesPage />} />
        <Route path="/datasets/:datasetId/versions" element={<DatasetVersionsPage />} />
        <Route path="/dataset-versions/:versionId" element={<DatasetVersionPage />} />
        <Route path="/dataset-versions/:versionId/mapping" element={<MappingPage />} />
        <Route
          path="/datasets/:datasetId/proposals"
          element={<Lazy element={<DatasetProposalsPage />} />}
        />
        <Route path="/dashboards" element={<Lazy element={<DashboardsPage />} />} />
        <Route path="/dashboards/:dashboardId" element={<Lazy element={<DashboardPage />} />} />
        <Route
          path="/dashboards/:dashboardId/add-widget"
          element={<Lazy element={<AddWidgetPage />} />}
        />
        <Route path="/dashboards/:dashboardId/ask" element={<Lazy element={<AskAIPage />} />} />
        <Route path="/ask" element={<Lazy element={<AskAIPage />} />} />
        <Route path="/members" element={<MembersPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
